import { forwardRef } from 'react'
import { Menu } from '@base-ui/react/menu'
import { BUTTON_DEFAULTS, buttonVariants, ButtonVariant, ButtonTone, ButtonSize, ButtonShape } from '../../button'
import { useGroupedButtonProps } from '../../button-group'
import { twMerge } from 'tailwind-merge'

/**
 * Props interface for MenuTrigger component.
 * Extends base-ui's Menu.Trigger.Props and includes button variant styling options.
 */
export interface MenuTriggerProps extends Menu.Trigger.Props {
  variant?: ButtonVariant
  tone?: ButtonTone
  size?: ButtonSize
  shape?: ButtonShape
  /** Drop the Button styling, for a trigger that is a whole region (a bar, a card) rather than a button. */
  unstyled?: boolean
  className?: string
}

/**
 * MenuTrigger - A button that opens the menu when clicked.
 *
 * This component extends the Button component's styling capabilities while
 * integrating with base-ui's Menu.Trigger for menu functionality.
 *
 * @example
 * ```tsx
 * <Menu.Trigger variant="primary" size="md">
 *   Open Menu
 * </Menu.Trigger>
 * ```
 */
const MenuTrigger = forwardRef<HTMLButtonElement, MenuTriggerProps>(function MenuTrigger(
  { variant, tone, size, shape, unstyled, className, children, ...props },
  ref,
) {
  const resolved = useGroupedButtonProps({ variant, tone, size }, BUTTON_DEFAULTS)
  const classes = buttonVariants({ ...resolved, shape })
  return (
    <Menu.Trigger
      ref={ref}
      // While its menu is open a neutral trigger holds the pressed fill, so it reads as the menu's
      // source. A primary trigger keeps its own fill.
      className={
        unstyled
          ? className
          : twMerge(
              'h-0 py-0',
              classes,
              resolved.variant !== 'primary' && 'data-[popup-open]:bg-element-active',
              className,
            )
      }
      {...props}
    >
      {children}
    </Menu.Trigger>
  )
})

export { MenuTrigger }
